import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CalculatorIcon } from "@heroicons/react/24/outline";
import { formatRate } from "../../utils/formatRate";
import { pricingTheme, type PricingTone } from "./pricingTheme";
import { PricingSectionHeader } from "./PricingSectionParts";
import { estimateMonthlyUsage, type EstimateInput } from "./estimate";
import { CHARGE_LABEL, type PublicRateCard } from "./rateCardTypes";

interface Props {
    tone: PricingTone;
    card: PublicRateCard;
}

type NumericField = Exclude<keyof EstimateInput, "carrier">;

const FIELDS: Array<{ key: NumericField; label: string; showWhen?: (card: PublicRateCard) => boolean }> = [
    { key: "aiMinutes", label: "AI voice minutes" },
    { key: "transfers", label: "Warm transfers", showWhen: (c) => c.voice.some((l) => l.chargeType === "CALL_TRANSFER") },
    { key: "phoneNumbers", label: "Phone numbers", showWhen: (c) => c.voice.some((l) => l.chargeType === "NUMBER_RENTAL") },
    { key: "agentMinutes", label: "Call-centre agent minutes" },
    {
        key: "callCentreNumbers",
        label: "Call-centre numbers",
        showWhen: (c) => c.callCentre.some((l) => l.chargeType === "CALL_CENTRE_RENTAL"),
    },
    { key: "smsSegments", label: "SMS segments", showWhen: (c) => c.sms.length > 0 },
];

const EMPTY: Record<NumericField, string> = {
    aiMinutes: "1000",
    transfers: "",
    phoneNumbers: "1",
    agentMinutes: "",
    callCentreNumbers: "",
    smsSegments: "",
};

export default function CostEstimator({ tone, card }: Props) {
    const t = pricingTheme(tone);
    const carriers = card.voice.filter((l) => l.chargeType === "VOICE_PER_MINUTE" && l.carrier);
    const [values, setValues] = useState<Record<NumericField, string>>(EMPTY);
    const [carrier, setCarrier] = useState<string | null>(carriers[0]?.carrier ?? null);

    const estimate = useMemo(
        () =>
            estimateMonthlyUsage(card, {
                aiMinutes: Number(values.aiMinutes),
                transfers: Number(values.transfers),
                phoneNumbers: Number(values.phoneNumbers),
                agentMinutes: Number(values.agentMinutes),
                callCentreNumbers: Number(values.callCentreNumbers),
                smsSegments: Number(values.smsSegments),
                carrier,
            }),
        [card, values, carrier],
    );

    const fields = FIELDS.filter((f) => !f.showWhen || f.showWhen(card));
    const totals = Object.entries(estimate.totals);

    return (
        <section aria-labelledby="pricing-estimator" className={t.section}>
            <PricingSectionHeader
                tone={tone}
                icon={CalculatorIcon}
                id="pricing-estimator"
                title="Estimate your monthly usage"
                subtitle="List prices before credits or a negotiated rate. Your plan is billed separately."
            />
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {carriers.length > 1 && (
                        <label className="sm:col-span-2">
                            <span className={`block text-sm font-medium ${t.subheading}`}>Carrier</span>
                            <select
                                value={carrier ?? ""}
                                onChange={(e) => setCarrier(e.target.value || null)}
                                className={`mt-1 w-full rounded-md px-3 py-2 text-sm ${t.input}`}
                            >
                                {carriers.map((l) => (
                                    <option key={l.carrier} value={l.carrier ?? ""}>
                                        {l.carrierLabel}
                                    </option>
                                ))}
                            </select>
                        </label>
                    )}
                    {fields.map((field) => (
                        <label key={field.key}>
                            <span className={`block text-sm font-medium ${t.subheading}`}>{field.label} / month</span>
                            <input
                                type="number"
                                min={0}
                                step={1}
                                inputMode="numeric"
                                value={values[field.key]}
                                onChange={(e) => setValues((prev) => ({ ...prev, [field.key]: e.target.value }))}
                                placeholder="0"
                                className={`mt-1 w-full rounded-md px-3 py-2 text-sm ${t.input}`}
                            />
                        </label>
                    ))}
                </div>

                <div className={t.card} aria-live="polite">
                    {estimate.lines.length === 0 ? (
                        <p className={`text-sm ${t.body}`}>Enter your expected usage to see an estimate.</p>
                    ) : (
                        <>
                            <dl className={`divide-y ${t.divider}`}>
                                {estimate.lines.map((line) => (
                                    <div key={line.chargeType} className="flex items-baseline justify-between gap-4 py-2">
                                        <dt className={`text-sm ${t.body}`}>
                                            {CHARGE_LABEL[line.chargeType]}{" "}
                                            <span className={`text-xs ${t.muted}`}>
                                                {line.quantity.toLocaleString()} × {formatRate(line.unitMinor, line.currency)}
                                            </span>
                                        </dt>
                                        <dd className={`text-sm font-semibold tabular-nums ${t.heading}`}>{formatRate(line.totalMinor, line.currency)}</dd>
                                    </div>
                                ))}
                            </dl>
                            <div className={`mt-3 border-t pt-3 ${t.divider}`}>
                                {totals.map(([currency, total]) => (
                                    <p key={currency} className="flex items-baseline justify-between">
                                        <span className={`text-sm font-semibold ${t.subheading}`}>Estimated usage{totals.length > 1 ? ` (${currency.toUpperCase()})` : ""}</span>
                                        <span className={`text-2xl font-extrabold tabular-nums ${t.heading}`}>{formatRate(total, currency)}</span>
                                    </p>
                                ))}
                            </div>
                        </>
                    )}
                    {estimate.committedSavingsMinor !== null && card.committedVoice && (
                        <p className={`mt-4 rounded-md px-3 py-2 text-sm ${t.accentBg}`}>
                            At this volume a committed plan is {formatRate(card.committedVoice.perMinuteMinor, card.currency)} / min — about{" "}
                            {formatRate(estimate.committedSavingsMinor, card.currency)} less a month.{" "}
                            <Link to="/contact" className="font-semibold underline underline-offset-2">
                                Talk to sales
                            </Link>
                        </p>
                    )}
                </div>
            </div>
        </section>
    );
}
